class QuarksMyStore extends HTMLElement {
	static mystore = null;
	static isFetching = false;
	static instances = [];

	constructor() {
		super();
		this.dataset.loading = true;
		QuarksMyStore.instances.push(this);
	}

	async connectedCallback() {
		this.storeDetails = this.querySelector('[data-store-details]');
		this.storeName = this.querySelector('[data-store-name]');
		this.storeAddress = this.querySelector('[data-store-address]');
		this.storeMaps = this.querySelector('[data-store-maps]');
		this.storePhoneHref = this.querySelector('[data-store-phone="href"]');
		this.storePhoneLabel = this.querySelector('[data-store-phone="label"]');
		this.storeHours = this.querySelector('[data-store-hours]');
		this.storeHref = this.querySelector('[data-store-href]');
		this.storeCollectionHref = this.querySelector('[data-store-collection-href]');
		this.storeId = this.getAttribute('data-store-id');

		if (QuarksMyStore.mystore !== null) {
			this.initStoreDetails();
			return;
		}

		if (QuarksMyStore.isFetching) return;

		try {
			await QuarksMyStore.getMyStore(this.storeId);
		} catch (error) {
			console.error('There was a problem initializing the store data:', error);
		}
	}

	disconnectedCallback() {
		const index = QuarksMyStore.instances.indexOf(this);
		if (index > -1) {
			QuarksMyStore.instances.splice(index, 1);
		}
	}

	static async getMyStore(storeId) {
		if (!storeId) return;
		if (QuarksMyStore.mystore) return;
		if (QuarksMyStore.isFetching) return;

		QuarksMyStore.isFetching = true;
		try {
			if (typeof ncol_api_url === "undefined") {
				throw new Error("[Reserve In-Store] Missing API URL.");
			}

			const cacheVersion = Math.floor(Date.now() / 3600000); // 60 minutes * 60 seconds/minute * 1000 ms/second
			const response = await fetch(`${ncol_api_url}/api/locations/${storeId}?v=${cacheVersion}`);
			if (!response.ok) throw new Error('Network response was not ok');
			const data = await response.json();
			if (!data || !data.store) throw new Error('No store data found');

			QuarksMyStore.mystore = data.store;
			QuarksMyStore.instances.forEach(instance => {
				instance.initStoreDetails();
			});
		} catch (error) {
			console.error('There was a problem fetching the store data:', error);

			QuarksMyStore.isFetching = false;
		}
	}

	initStoreDetails() {
		if (!QuarksMyStore.mystore) return;
		this.mystore = QuarksMyStore.mystore;

		this.storeName.innerHTML = this.mystore.name ? this.mystore.name.replace(' - ', '<br/>') : '';

		this.storeAddress.innerHTML = `
			${this.mystore.city}, ${this.mystore.province_code}
		`.replace(/\s+/g, ' ').trim();

		if (this.mystore.phone) {
			const phoneNumber = this.mystore.phone.replace(/(?:\+1)*(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
			this.storePhoneLabel.textContent = phoneNumber;
			this.storePhoneHref.setAttribute('aria-label', `Call ${phoneNumber}`);
			this.storePhoneHref.href = `tel:${this.mystore.phone || ''}`;
		} else {
			this.storePhoneLabel.remove();
			this.storePhoneHref.remove();
		}

		if (this.mystore.latitude && this.mystore.longitude) {
			this.storeMaps.setAttribute("href", "https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent(`${this.mystore.address1}, ${this.mystore.city}, ${this.mystore.province_code}, ${this.mystore.zip}`).replace(/%20/g, "+"));
			this.storeMaps.removeAttribute('hidden');
			this.storeMaps.setAttribute('target', '_blank');
		} else this.storeMaps.remove();

		const addStoreHours = () => {
			try {
				if (!this.mystore.store_hours || !this.mystore.store_hours.length) throw new Error('No store hours data');
	
				const today = new Date().getDay();
				const hoursToday = this.mystore.store_hours[today];
				if (!hoursToday) throw new Error('No store hours for today');
	
				const hoursText = hoursToday.split('~');
	
				if (hoursText[1].trim().toLowerCase() === 'closed') {
					this.storeHours.textContent = 'Closed today';
					return;
				}
	
				const openCloseTime = hoursText[1].split('–');
				const openTime = openCloseTime[0].trim();
				const closeTime = openCloseTime[1].trim();
				this.storeHours.textContent = `Open today ${openTime} – ${closeTime}`;
			} catch(error) {
				this.storeHours.remove();
			}
		};
		addStoreHours();

		this.storeHref.href = `/pages/locations/${this.mystore.handle || this.storeId || ''}`;
		this.storeCollectionHref.href = `/collections/${this.mystore.handle || this.storeId || ''}`;

		this.storeDetails.removeAttribute('hidden');
	}
}
customElements.define('quarks-my-store', QuarksMyStore);

class QuarksMyStoreModal extends HTMLElement {
	constructor() {
		super();
		this.dataset.loading = true;
		this.onModalClosed = this.handleModalClosed.bind(this);
	}

	connectedCallback() {
		this.form = this.querySelector('form');
		this.form.addEventListener('submit', this.onSubmit.bind(this));
		this.form.setAttribute('aria-busy', 'true');
		this.modal = this.closest('modal-dialog');

		this.currentId = this.form.querySelector('input[name="attributes[mystore]"]').value;
		document.body.addEventListener('modalClosed', this.onModalClosed);

		const modalOpener = document.querySelectorAll('[data-modal="#PopupModal-my-store"] button');
		modalOpener.forEach(button => {
			button.addEventListener('click', () => {
				this.captureDisclosureState(button);
				this.initLocations();
			});
		});
	}

	disconnectedCallback() {
		document.body.removeEventListener('modalClosed', this.onModalClosed);
	}

	captureDisclosureState(button) {
		const disclosureElement = button.closest('details-disclosure');
		const detailsElement = disclosureElement?.querySelector('details');

		this.disclosureToRestore = detailsElement?.hasAttribute('open') ? detailsElement : null;
	}

	handleModalClosed() {
		if (!this.disclosureToRestore) return;
		if (this.modal && !this.modal.hasAttribute('open')) return;

		this.disclosureToRestore.setAttribute('open', '');
		this.disclosureToRestore = null;
	}
	
	async initLocations() {
		if (this.initialized) return;
		this.initialized = true;

		const storeLocationsList = this.querySelector('store-locations');
		try {
			await storeLocationsList?.load();
		} catch (error) {
			console.error('There was a problem loading store locations:', error);
		}

		this.selectStoreButtons = this.querySelectorAll('button[data-select-my-store]');
		this.selectStoreButtons.forEach(button => {
			// Set current store indicator.
			const storeId = button.getAttribute('data-id');
			if (this.currentId && this.currentId === storeId) {
				button.setAttribute('aria-current', 'true');
			}
			
			button.addEventListener('click', this.onSelectStore.bind(this));
		});

		this.querySelector('[data-loading]').remove();
		this.removeAttribute('data-loading');
		this.form.removeAttribute('aria-busy');
	}

	async onSubmit(event) {
		event.preventDefault();
		const formData = new FormData(this.form);
		
		try {
			const response = await fetch(this.form.action, {
				method: 'POST',
				body: formData
			});
			if (!response.ok) throw new Error('Network response was not ok');
			window.location.reload();
		} catch (error) {
			console.error('There was a problem with the fetch operation:', error);
		}
	}

	onSelectStore(event) {
		const button = event.currentTarget;
		const storeId = button.getAttribute('data-id');
		const shopifyId = button.getAttribute('data-shopify-id');
		const storeName = button.getAttribute('data-name');
		const storeBrand = button.getAttribute('data-brand');

		const reset = this.currentId === storeId;
		this.form.querySelector('input[name="attributes[mystore]"]').value = reset ? '' : storeId;
		this.form.querySelector('input[name="attributes[mystore_id]"]').value = reset ? '' : shopifyId;
		this.form.querySelector('input[name="attributes[mystore_name]"]').value = reset ? '' : storeName;
		this.form.querySelector('input[name="attributes[mystore_brand]"]').value = reset ? '' : storeBrand;

		this.form.requestSubmit();
	}
}
customElements.define('quarks-mystore-modal', QuarksMyStoreModal);